import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import { CMS } from '../../interfaces/CMS';
import { PSP } from '../../interfaces/PSP';
import { UserState } from '../../interfaces/states/UserState';
import { WebShop } from '../../interfaces/WebShop';
import { INTERCOM_APP_ID } from '../../lib/constants/central-api';

interface OnboardingStagesMapInterface {
  name: string;
  displayTitle: string;
  followedBy: string[];
  stepNumber: number;
}

interface CompanyDetails {
  name?: string;
  shop_id?: string;
  currency?: string;
  currency_symbol?: string;
  shop_slug?: string;
  location?: string;
  is_active?: boolean;
  initial?: string;
  webshop_url?: string;
  transaction_per_month?: string;
}

const defaultCompanySettings = {
  theme_color: '',
  font_color: '',
  background_image: '',
  company_logo: '',
  company_icon: '',
  facebook_pixel_id: '',
  tiktok_pixel_id: '',
  google_analytics_id: '',
  default_shipping_cost: '',
  min_free_shipping: '',
  fixed_shipping: '',
  send_monthly_newsletter: '',
  background_color: '',
  fascheckout_ui: '',
  facebook_access_token: '',
  tiktok_test_event_code: '',
  webshop_url: '',
  tiktok_access_token: '',
  setting: '',
  shop_name: '',
  shop_slug: '',
  shop_initials: '',
  currency: '',
};

type onboardingOAuthCallback = string | null;

export const OnboardingStagesMap: {
  [index: string]: OnboardingStagesMapInterface;
} = Object.freeze({
  SigningUp: {
    name: 'SigningUp',
    displayTitle: 'Register',
    followedBy: [
      'ProfileUpdate',
      'SubmittingCompany',
      'IntegratingCMS1',
      'SettingCMSConnection',
      'IntegratingPSP1',
      'SettingPSPConnection',
      'WebshopTransactions',
    ],
    stepNumber: 1,
  },
  SigningIn: {
    name: 'SigningIn',
    displayTitle: 'SignIn',
    followedBy: [
      'ProfileUpdate',
      'SubmittingCompany',
      'IntegratingCMS1',
      'SettingCMSConnection',
      'IntegratingPSP1',
      'SettingPSPConnection',
      'WebshopTransactions',
    ],
    stepNumber: 2,
  },
  ProfileUpdate: {
    name: 'ProfileUpdate',
    displayTitle: 'UpdateProfile',
    followedBy: [
      'SubmittingCompany',
      'IntegratingCMS1',
      'SettingCMSConnection',
      'IntegratingPSP1',
      'SettingPSPConnection',
      'WebshopTransactions',
    ],
    stepNumber: 3,
  },
  SubmittingCompany: {
    name: 'SubmittingCompany',
    displayTitle: 'SetupCompanyDetails',
    followedBy: [
      'IntegratingCMS1',
      'SettingCMSConnection',
      'IntegratingPSP1',
      'SettingPSPConnection',
      'WebshopTransactions',
    ],
    stepNumber: 4,
  },
  IntegratingCMS1: {
    name: 'IntegratingCMS1',
    displayTitle: 'IntegratingCMS',
    followedBy: [
      'SettingCMSConnection',
      'IntegratingPSP1',
      'SettingPSPConnection',
      'WebshopTransactions',
    ],
    stepNumber: 5,
  },
  SettingCMSConnection: {
    name: 'SettingCMSConnection',
    displayTitle: 'SettingCMSConnection',
    followedBy: [
      'IntegratingPSP1',
      'SettingPSPConnection',
      'WebshopTransactions',
    ],
    stepNumber: 6,
  },
  IntegratingPSP1: {
    name: 'IntegratingPSP1',
    displayTitle: 'SelectingPaymentProviders',
    followedBy: ['SettingPSPConnection', 'WebshopTransactions'],
    stepNumber: 7,
  },
  SettingPSPConnection: {
    name: 'SettingPSPConnection',
    displayTitle: 'SettingPSPConnection',
    followedBy: ['WebshopTransactions'],
    stepNumber: 8,
  },
  WebshopTransactions: {
    name: 'WebshopTransactions',
    displayTitle: 'WebshopTransactions',
    followedBy: [],
    stepNumber: 9,
  },
});

const ai_onboarding_steps = [
  {
    number: 1,
    title: 'BusinessType',
    isCompleted: false,
  },
  {
    number: 2,
    title: 'AddClient',
    isCompleted: false,
  },
  {
    number: 3,
    title: 'ConnectAccounts',
    isCompleted: false,
  },
  {
    number: 4,
    title: 'InstallPlugin',
    isCompleted: false,
  },
  {
    number: 5,
    title: 'GetStarted',
    isCompleted: false,
  },
];

const makeInitialStagesCompleted = () => {
  const result: { [key: string]: boolean } = {};

  Object.keys(OnboardingStagesMap).forEach((key) => (result[key] = false));

  return result;
};

const initialStagesCompleted: { [key: string]: boolean } = Object.freeze(
  makeInitialStagesCompleted()
);

const initialState: UserState = {
  company: {
    name: '',
    shop_id: '',
    currency: '',
    currency_symbol: '',
    shop_slug: '',
    webshop_url: '',
    transaction_per_month: '',
    location: '',
    is_active: false,
    initial: '',
    webshops: [],
    cms: [],
    psp: [],
    cmsSelected: '',
    pspSelected: '',
    preSelectedCMS_PSP: { preSelectedCMS: '', preSelectedPSP: '' },
    alreadyAddedPSP: [],
    settings: defaultCompanySettings,
  },

  shop: {
    isProductWizardOpen: false,
    shouldShowProductWizard: false,
    hasInitPSP: false,
    availableTaraSubscriptionPackages: {
      apiData: [],
      structuredPackageData: [],
      syncTS: 0,
    },
    taraSubscriptionDetails: {
      currentSubscriptionStatus: 'none',
    },
    taraTrialDetails: {
      is_trial: false,
      trial_available_days: undefined,
    },
  },

  stats: {
    ai_tracker_configured_timestamp: '',
    ai_tracker_api_key: '',
    ai_tracker_access_token: '',
    ai_tracker_access_token_expires_in: '',
    is_tracker_classes_configured: false,
    is_trackable: false,
    ai_tracker_access_token_status: 'null',
    ai_tracker_integration_age: '',
  },

  profile: {
    seller_id: '',
    user_id: '',
    role: 'seller',
    all_shops: [],
    active_shop_slug: '',
    first_name: '',
    last_name: '',
    country_code: '',
    phone: '',
    email: '',
    language: '',
    custom_openai_key: '',
  },

  merchant: {
    id: '',
    merchant_slug: '',
    merchant_name: '',
    fastcheckout_domain: '',
    dashboard_domain: '',
    config: {
      main_brand_color: '',
      secondary_brand_color: '',
      main_brand_hover_color: '',
      header_gradient_color_1: '',
      header_gradient_color_2: '',
      main_btn_color: '',
      main_btn_hover_color: '',
      main_btn_text_color: '',
      main_text_color: '',
      font_family: '',
      merchant_name: '',
      monthly_newsletter_text: '',
      signup_text1: '',
      merchant_favicon: '',
      merchant_icon: '',
      merchant_logo_light: '',
      merchant_logo_dark: '',
      terms_url: '',
      privacy_url: '',
      faq_url: '',
      how_fastcheckout_works_url: '',
      login_image: '',
      signup_image: '',
      is_onboarding_cms_hidden: 'false',
      is_onboarding_psp_hidden: 'false',
    },
  },

  onboarding: {
    onboarding_object: { available_psp: [], available_cms: [] },
    stagesCompleted: initialStagesCompleted,
    currentStage: '',
    availableCMS: [],
    availablePSP: [],
    paymentMethods: {},
    onboarding_completed: false,
  },

  ai_onboarding: {
    currentStep: 1,
    steps: ai_onboarding_steps,
    is_ai_onboarding_completed: false,
    is_ai_onboarding_inprogress: false,
    is_onboarding_metrics_active: false,
    is_integration_connected: false,
    selected_integrate: 'standard',
    userPrompt: null,
    hasShownCalendly: false,
    onboardingOAuthCallback: null as onboardingOAuthCallback,
    metricsConsent: false,
    hasSeenAskTARAPopup: false,
    companyStepDraft: null as {
      userRole: 'seller' | 'agency';
      /** For agency: connected shops { url, name, slug }; for seller: legacy sites/url */
      connectedShops?: { url: string; name: string; slug: string }[];
      sites?: string[];
      url?: string;
    } | null,
    brandStepDraft: null as {
      brandColor: string | null;
      logoPreviewDataUrl: string | null;
    } | null,
  },

  autofillInregrations: null,
  pspConnections: null,
  referral: '',
  preUserInvitation: {
    id: null,
    data: null,
  },
  intercom: {
    app_id: INTERCOM_APP_ID,
    isFirstBootComplete: false,
    cms: 'null',
    cmsdatafilled: 'false',
    psp: 'null',
    pspdatafilled: 'false',
  },
  isTrackingScriptConnectionSuccess: undefined,
};

export const userSlice = createSlice({
  name: 'user',
  initialState,
  reducers: {
    setUserCompanyDetails: (state, action: PayloadAction<CompanyDetails>) => {
      if (action.payload.name) state.company.name = action.payload.name;
      if (action.payload.currency)
        state.company.currency = action.payload.currency;
      if (action.payload.currency_symbol)
        state.company.currency_symbol = action.payload.currency_symbol;
      if (action.payload.shop_id)
        state.company.shop_id = action.payload.shop_id;
      if (action.payload.shop_slug)
        state.company.shop_slug = action.payload.shop_slug;
      if (action.payload.webshop_url)
        state.company.webshop_url = action.payload.webshop_url;
      if (action.payload.transaction_per_month)
        state.company.transaction_per_month =
          action.payload.transaction_per_month;
      if (action.payload.location)
        state.company.location = action.payload.location;
      if (action.payload.is_active)
        state.company.is_active = action.payload.is_active;
      if (action.payload.initial)
        state.company.initial = action.payload.initial;
    },

    setUserCompanySettings: (state, action: PayloadAction<any>) => {
      state.company.settings = { ...state.company.settings, ...action.payload };
    },

    setAiTrackerAccessTokenStatus: (
      state,
      action: PayloadAction<'pending' | 'success' | 'error' | 'null'>
    ) => {
      state.stats.ai_tracker_access_token_status = action.payload;
    },

    updateProfile: (state, action: PayloadAction<any>) => {
      const {
        seller_id,
        user_id,
        role,
        all_shops,
        active_shop_slug,
        first_name,
        last_name,
        country_code,
        phone,
        email,
        language,
        custom_openai_key,
      } = action.payload;

      if (seller_id) state.profile.seller_id = seller_id;
      if (user_id) state.profile.user_id = user_id;
      if (role) state.profile.role = role;
      if (all_shops) state.profile.all_shops = all_shops;
      if (active_shop_slug) state.profile.active_shop_slug = active_shop_slug;
      if (first_name) state.profile.first_name = first_name;
      if (last_name) state.profile.last_name = last_name;
      if (country_code) state.profile.country_code = country_code;
      if (phone) state.profile.phone = phone;
      if (email) state.profile.email = email;
      if (language) state.profile.language = language;
      if (custom_openai_key || custom_openai_key === '')
        state.profile.custom_openai_key = custom_openai_key;
    },

    updateMerchant: (state, action: PayloadAction<any>) => {
      const { id, slug, name, settings } = action.payload;

      if (id) state.merchant.id = id;
      if (slug) state.merchant.merchant_slug = slug;
      if (name) state.merchant.merchant_name = name;
      if (settings?.general?.fastcheckout_domain)
        state.merchant.fastcheckout_domain =
          settings?.general?.fastcheckout_domain;
      if (settings?.general?.dashboard_domain)
        state.merchant.dashboard_domain = settings?.general?.dashboard_domain;
      if (settings?.configs) state.merchant.config = settings?.configs;
    },

    updateIntercom: (state, action: PayloadAction<any>) => {
      const { isFirstBootComplete, cms, cmsdatafilled, psp, pspdatafilled } =
        action.payload;
      if (isFirstBootComplete)
        state.intercom.isFirstBootComplete = isFirstBootComplete;
      if (cms) state.intercom.cms = cms;
      if (cmsdatafilled) state.intercom.cmsdatafilled = cmsdatafilled;
      if (psp) state.intercom.psp = psp;
      if (pspdatafilled) state.intercom.pspdatafilled = pspdatafilled;
    },

    addWebshop: (state, action: PayloadAction<WebShop>) => {
      state.company.webshops = state.company.webshops.concat(action.payload);
    },

    setHasShownCalendly: (state, action: PayloadAction<boolean>) => {
      state.ai_onboarding.hasShownCalendly = action.payload;
    },

    setAiOnboardingUserPrompt: (
      state,
      action: PayloadAction<string | null>
    ) => {
      state.ai_onboarding.userPrompt = action.payload;
    },

    setAiOnboardingNextStep: (state, action: PayloadAction<number>) => {
      if (
        action.payload > state.ai_onboarding.steps?.length ||
        action.payload < 1
      ) {
        return;
      }
      state.ai_onboarding.currentStep = action.payload;
    },

    setIsAiOnboardingCompleted: (state, action: PayloadAction<boolean>) => {
      state.ai_onboarding.is_ai_onboarding_completed = action.payload;
    },

    setAiOnboardingStepCompleted: (
      state,
      action: PayloadAction<{ stepNumber: number; isCompleted: boolean }>
    ) => {
      state.ai_onboarding.steps[action.payload.stepNumber - 1].isCompleted =
        action.payload.isCompleted;
    },

    setAiOnboardingInprogress: (state, action: PayloadAction<boolean>) => {
      state.ai_onboarding.is_ai_onboarding_inprogress = action.payload;
    },

    setOnboardingOAuthCallbackParams(state, action: PayloadAction<string>) {
      state.ai_onboarding.onboardingOAuthCallback = action.payload;
    },

    clearOnboardingOAuthCallbackParams(state) {
      state.ai_onboarding.onboardingOAuthCallback = null;
    },

    setMetricsConsent: (state, action: PayloadAction<boolean>) => {
      state.ai_onboarding.metricsConsent = action.payload;
    },

    setHasSeenAskTARAPopup: (state, action: PayloadAction<boolean>) => {
      state.ai_onboarding.hasSeenAskTARAPopup = action.payload;
    },

    setIsOnboardingMetricsActive: (state, action: PayloadAction<boolean>) => {
      state.ai_onboarding.is_onboarding_metrics_active = action.payload;
    },

    setCompanyStepDraft: (
      state,
      action: PayloadAction<{
        userRole: 'seller' | 'agency';
        connectedShops?: { url: string; name: string; slug: string }[];
        sites?: string[];
        url?: string;
      } | null>
    ) => {
      state.ai_onboarding.companyStepDraft = action.payload;
    },

    setBrandStepDraft: (
      state,
      action: PayloadAction<{
        brandColor: string | null;
        logoPreviewDataUrl: string | null;
      } | null>
    ) => {
      state.ai_onboarding.brandStepDraft = action.payload;
    },

    /** Migrate persisted 4-step onboarding state to 5-step structure */
    migrateOnboardingSteps: (state) => {
      if (state.ai_onboarding.steps.length === 5) return;
      const oldSteps = state.ai_onboarding.steps;
      state.ai_onboarding.steps = ai_onboarding_steps.map((step) => {
        // Map old completions: old step 1 → new step 1+2, old step 2 → new step 3
        if (step.number <= 2 && oldSteps[0]?.isCompleted) {
          return { ...step, isCompleted: true };
        }
        if (step.number === 3 && oldSteps[1]?.isCompleted) {
          return { ...step, isCompleted: true };
        }
        if (step.number === 5 && oldSteps[3]?.isCompleted) {
          return { ...step, isCompleted: true };
        }
        return step;
      });
    },

    setAvailableTaraSubscriptionPackages: (
      state,
      action: PayloadAction<{ apiData: any[]; structuredPackageData: any[] }>
    ) => {
      state.shop.availableTaraSubscriptionPackages = {
        apiData: action.payload?.apiData,
        structuredPackageData: action.payload?.structuredPackageData,
        syncTS: new Date().getTime(),
      };
    },

    setTaraSubscriptionDetails: (state, action: PayloadAction<any>) => {
      state.shop.taraSubscriptionDetails = action.payload;
    },

    setTaraTrialDetails: (state, action: PayloadAction<any>) => {
      state.shop.taraTrialDetails = action.payload;
    },

    setAiOnboardingSelectedIntegrate: (
      state,
      action: PayloadAction<string>
    ) => {
      state.ai_onboarding.selected_integrate = action.payload;
    },

    setIsAiOnboardingIntegrationConnected: (
      state,
      action: PayloadAction<boolean>
    ) => {
      state.ai_onboarding.is_integration_connected = action.payload;
    },

    updateOnboardingStage: (state, action: PayloadAction<string>) => {
      if (action.payload in OnboardingStagesMap) {
        state.onboarding.currentStage = action.payload;

        // if we go back, erase the data for the stages before
        const followingStages = OnboardingStagesMap[action.payload].followedBy;

        for (const followingStage of followingStages) {
          state.onboarding.stagesCompleted[followingStage] = false;
        }
      }
    },

    getOnboardingObject: (state, action: PayloadAction<any>) => {
      state.onboarding.onboarding_object = action.payload;

      const reuslt = action.payload;

      const psp = reuslt.available_psp?.map((el: object, i: number) => {
        return Object.keys(el)[0].toLowerCase();
      });
      const cms = reuslt.available_cms?.map((el: object, i: number) => {
        return Object.keys(el)[0].toLowerCase();
      });

      state.onboarding.availablePSP = psp;
      state.onboarding.availableCMS = cms;
    },

    setAvailableCMS: (state, action: PayloadAction<any>) => {
      const result = action.payload;
      const cms = result?.map((el: any) => {
        return el?.slug;
      });

      state.onboarding.onboarding_object.available_cms = result;
      state.onboarding.availableCMS = cms;
    },

    setAvailablePSP: (state, action: PayloadAction<any>) => {
      const result = action.payload;
      const psp = result?.map((el: any) => {
        return el?.slug;
      });

      state.onboarding.onboarding_object.available_psp = result;
      state.onboarding.availablePSP = psp;
    },

    // setAvailableCMS: (state, action: PayloadAction<CMS[]>) => {
    //   state.onboarding.availableCMS = action.payload;
    // },

    // setAvailablePSP: (state, action: PayloadAction<PSP[]>) => {
    //   state.onboarding.availablePSP = action.payload;
    // },

    addCMS: (state, action: PayloadAction<CMS>) => {
      state.company.cms = state.company.cms.concat(action.payload);
    },

    addSelectedCMS: (state, action: PayloadAction<string>) => {
      state.company.cmsSelected = action.payload;
    },

    addPSP: (state, action: PayloadAction<PSP>) => {
      state.company.psp = state.company.psp.concat(action.payload);
    },

    addSelectedPSP: (state, action: PayloadAction<string>) => {
      state.company.pspSelected = action.payload;
    },

    removePSP: (state, action: PayloadAction<{ id: string }>) => {
      state.company.psp = state.company.psp.filter(
        (provider) => provider.id !== action.payload.id
      );
    },

    setStageComplete: (
      state,
      action: PayloadAction<{ stage: string; isCompleted: boolean }>
    ) => {
      if (action.payload.stage in OnboardingStagesMap) {
        state.onboarding.stagesCompleted[action.payload.stage] =
          action.payload.isCompleted;
      }
    },

    setPreSelectedCMS_PSP: (
      state,
      action: PayloadAction<{ type: 'cms' | 'psp'; name: string }>
    ) => {
      if (action.payload.type === 'cms') {
        state.company.preSelectedCMS_PSP.preSelectedCMS = action.payload.name;
      } else if (action.payload.type === 'psp') {
        state.company.preSelectedCMS_PSP.preSelectedPSP = action.payload.name;
      }
    },

    completeOnboarding: (state, action: PayloadAction<boolean>) => {
      state.onboarding.onboarding_completed = action.payload;
    },

    setPaymentMethods: (
      state,
      action: PayloadAction<{
        [key: string]: string[];
      }>
    ) => {
      state.onboarding.paymentMethods = action.payload;
    },

    setAutofillInregrations: (state, action: PayloadAction<any>) => {
      state.autofillInregrations = action.payload;
    },

    setPSPConnections: (state, action: PayloadAction<any>) => {
      state.pspConnections = action.payload;
    },

    setAlreadyAddedPSP: (state, action: PayloadAction<any>) => {
      state.company.alreadyAddedPSP = action.payload;
    },

    setIsProductWizardOpen: (state, action: PayloadAction<any>) => {
      state.shop.isProductWizardOpen = action.payload;
    },

    setShouldShowProductWizard: (state, action: PayloadAction<any>) => {
      state.shop.shouldShowProductWizard = action.payload;
    },
    setAiTrackerConfiguredTimestamp: (state, action: PayloadAction<any>) => {
      state.stats.ai_tracker_configured_timestamp = action.payload;
    },

    setAiTrackerAPIKey: (state, action: PayloadAction<any>) => {
      state.stats.ai_tracker_api_key = action.payload;
    },

    setAiTrackerIntegrationAge: (state, action: PayloadAction<any>) => {
      state.stats.ai_tracker_integration_age = action.payload;
    },

    setAiTrackerAccessToken: (state, action: PayloadAction<any>) => {
      state.stats.ai_tracker_access_token = action.payload;
    },

    setIsTrackerClassesConfigured: (state, action: PayloadAction<boolean>) => {
      state.stats.is_tracker_classes_configured = action.payload;
    },

    setHasInitPSP: (state, action: PayloadAction<boolean>) => {
      state.shop.hasInitPSP = action.payload;
    },
    setAiTrackerAccessTokenExpireDate: (state, action: PayloadAction<any>) => {
      state.stats.ai_tracker_access_token_expires_in = action.payload;
    },

    setReferral: (state, action: PayloadAction<string>) => {
      state.referral = action.payload;
    },

    setPreUserInvitation: (state, action: PayloadAction<any>) => {
      if (action.payload.id) state.preUserInvitation.id = action.payload.id;
      if (action.payload.data)
        state.preUserInvitation.data = action.payload.data;
    },

    clearPreUserInvitation: (state) => {
      state.preUserInvitation = initialState.preUserInvitation;
    },

    setIsTrackable: (state, action: PayloadAction<boolean>) => {
      state.stats.is_trackable = action.payload;
    },

    setIsTrackingScriptConnectionSuccess: (
      state,
      action: PayloadAction<'true' | 'false' | undefined>
    ) => {
      state.isTrackingScriptConnectionSuccess = action.payload;
    },

    clearStats: (state) => {
      state.stats = initialState.stats;
    },

    clearUserSlice: (state) => {
      state.company = initialState.company;
      state.shop = initialState.shop;
      state.onboarding = initialState.onboarding;
      state.profile = initialState.profile;
      state.stats = initialState.stats;
      state.ai_onboarding = initialState.ai_onboarding;
      // state.merchant = initialState.merchant;
      state.intercom = initialState.intercom;
      state.autofillInregrations = initialState.autofillInregrations;
      state.pspConnections = initialState.pspConnections;
      state.referral = initialState.referral;
      state.isTrackingScriptConnectionSuccess =
        initialState.isTrackingScriptConnectionSuccess;
    },
  },
});

export const {
  updateProfile,
  addWebshop,
  updateOnboardingStage,
  addCMS,
  addPSP,
  removePSP,
  setStageComplete,
  setAvailableCMS,
  setAvailablePSP,
  setUserCompanyDetails,
  setUserCompanySettings,
  completeOnboarding,
  getOnboardingObject,
  setPaymentMethods,
  setAutofillInregrations,
  setPSPConnections,
  addSelectedCMS,
  addSelectedPSP,
  updateIntercom,
  clearStats,
  clearUserSlice,
  setAlreadyAddedPSP,
  setIsProductWizardOpen,
  setShouldShowProductWizard,
  setPreSelectedCMS_PSP,
  setHasInitPSP,
  updateMerchant,
  setAiTrackerConfiguredTimestamp,
  setAiTrackerAPIKey,
  setAiTrackerIntegrationAge,
  setAiTrackerAccessToken,
  setAiOnboardingNextStep,
  setIsAiOnboardingCompleted,
  setAiOnboardingStepCompleted,
  setAiOnboardingInprogress,
  setAiOnboardingSelectedIntegrate,
  setIsAiOnboardingIntegrationConnected,
  setAiTrackerAccessTokenExpireDate,
  setReferral,
  setPreUserInvitation,
  clearPreUserInvitation,
  setIsTrackable,
  setIsTrackerClassesConfigured,
  setAvailableTaraSubscriptionPackages,
  setTaraSubscriptionDetails,
  setTaraTrialDetails,
  setAiTrackerAccessTokenStatus,
  setIsTrackingScriptConnectionSuccess,
  setAiOnboardingUserPrompt,
  setHasShownCalendly,
  setOnboardingOAuthCallbackParams,
  clearOnboardingOAuthCallbackParams,
  setMetricsConsent,
  setHasSeenAskTARAPopup,
  setIsOnboardingMetricsActive,
  setCompanyStepDraft,
  setBrandStepDraft,
  migrateOnboardingSteps,
} = userSlice.actions;
export const userStateReducer = userSlice.reducer;
